/**
 * OAuth Client Profiles - tenant-managed, reusable OAuth client identities
 * that TrustLogix uses when it connects to MCP services.
 *
 * Mock-only until the backend exposes a client-profile registry. Everything
 * below is a proposed contract, not a description of a live endpoint; the
 * mock registry (`src/api/mock-data/oauth-client-profiles`) answers every
 * method inside a `<MockScope>` and nothing calls a real host.
 *
 * Four things stay apart, so no single status can stand in for another:
 *  - the profile record and its lifecycle (`DRAFT | AVAILABLE | ARCHIVED`);
 *  - validation evidence for the profile's own inputs, bound to their
 *    fingerprint and revision - never a service authorization;
 *  - usage references from saved MCP connection configurations;
 *  - eligibility for one form context (method, purpose, issuer) - never
 *    authorization, and never a runtime result.
 *
 * A profile references credentials held by the managed credential system; it
 * never carries a secret, a private key or a token.
 */

export type ProfileLifecycle = "DRAFT" | "AVAILABLE" | "ARCHIVED";

/** A TrustLogix selection restriction - never OAuth consent. */
export type ProfilePurpose = "METADATA_DISCOVERY" | "RUNTIME_ACCESS";

export type ProfileCheckState =
  | "NOT_CHECKED"
  | "CHECKING"
  | "PASSED"
  | "FAILED"
  | "STALE"
  | "UNAVAILABLE"
  | "NOT_APPLICABLE";

export type ProfileRegistrationKind =
  | "CIMD"
  | "EXISTING_OAUTH_CLIENT"
  | "LEGACY_READ_ONLY";

/** Fields may be incomplete while a profile is a draft. */
export type ProfileRegistration =
  | {
      kind: "CIMD";
      metadataSource: "EXISTING_PUBLISHED_DOCUMENT";
      /** The metadata document URL - which is also the OAuth client ID. */
      clientIdUrl?: string;
    }
  | {
      kind: "EXISTING_OAUTH_CLIENT";
      clientId?: string;
      /** The provider integration the application is registered with. */
      providerId?: string;
      /** Its display name, resolved by the server. */
      providerName?: string;
      /**
       * The issuer resolved from that integration when saved. Records saved
       * before provider references existed carry only this.
       */
      issuer?: string;
      /** The integration's display name, resolved by the server. */
      issuerName?: string;
    }
  | {
      /** Preserved through an adapter; managed where it was created. */
      kind: "LEGACY_READ_ONLY";
      originalMethod: "DCR";
      clientId: string;
      issuer?: string;
      sourceRef: string;
      sourceName: string;
    };

export type CredentialAvailability =
  | "USABLE"
  | "MISSING"
  | "RESTRICTED"
  | "NOT_CHECKED"
  | "NOT_REQUIRED";

/** A reference into the managed credential system - never the credential. */
export interface ClientAuthenticationBinding {
  /** Declared client-authentication method, as the backend adapter names it. */
  method?: string;
  managedCredentialRef?: string;
  referenceLabel?: string;
  availability: CredentialAvailability;
}

export type ProfileCheckCode =
  | "METADATA_RETRIEVAL"
  | "DOCUMENT_VALIDATION"
  | "CLIENT_ID_MATCH"
  | "MANAGED_AUTHENTICATION"
  | "DETAILS_COMPLETE"
  | "ISSUER_BINDING";

/** Safe, localizable reason codes - never a raw remote body. */
export type ProfileReasonCode =
  | "INVALID_URL"
  | "UNSAFE_DESTINATION"
  | "REDIRECT_REJECTED"
  | "UNREACHABLE"
  | "NOT_METADATA_DOCUMENT"
  | "CLIENT_ID_MISMATCH"
  | "FORBIDDEN_CREDENTIAL_DATA"
  | "AUTH_REFERENCE_MISSING"
  | "AUTH_REFERENCE_RESTRICTED"
  | "KEY_BINDING_NOT_ESTABLISHED"
  | "ISSUER_UNKNOWN"
  | "ISSUER_NOT_CONFIGURED"
  | "ISSUER_MISMATCH"
  | "DETAILS_INCOMPLETE"
  | "DOCUMENT_CHANGED"
  | "CHECK_AGED"
  | "INPUT_CHANGED"
  | "VALIDATION_UNAVAILABLE";

export interface ProfileCheck {
  code: ProfileCheckCode;
  state: ProfileCheckState;
  reasonCode?: ProfileReasonCode;
}

export interface ProfileValidation {
  state: ProfileCheckState;
  checks: ProfileCheck[];
  /** The profile revision the checks ran against (absent for a candidate). */
  profileRevision?: string;
  /** Fingerprint of the inputs the checks ran against. */
  inputFingerprint: string;
  /** Opaque, bound to `inputFingerprint`; the server verifies it on save. */
  validationRef?: string;
  checkedAt?: string;
  documentFingerprint?: string;
  /** e.g. "draft-ietf-oauth-client-id-metadata-document-02". */
  specificationProfile?: string;
  /** Set when `state` is STALE: why the recorded result no longer counts. */
  staleReason?: Extract<
    ProfileReasonCode,
    "DOCUMENT_CHANGED" | "CHECK_AGED" | "INPUT_CHANGED"
  >;
}

/** The sanitized public metadata the backend observed - rendered as text. */
export interface ProfileMetadataPreview {
  clientName?: string;
  clientId: string;
  tokenEndpointAuthMethod?: string;
  grantTypes: string[];
  redirectUris: string[];
  jwksUri?: string;
  /** Allowlisted public fields only; never rendered as markup. */
  document: Record<string, unknown>;
  fetchedAt: string;
  documentFingerprint: string;
}

export type UsageCompleteness = "COMPLETE" | "PARTIAL" | "UNKNOWN";

export interface ProfileUsageSummary {
  /** Distinct saved MCP connection configurations - null when unknown. */
  distinctConnectionCount: number | null;
  gatewayOrDiscoveryReferenceCount: number | null;
  completeness: UsageCompleteness;
}

export type ProfileAction =
  | "EDIT"
  | "VALIDATE"
  | "ARCHIVE"
  | "RESTORE"
  | "VIEW_USAGE";

export type ProfileActivityType =
  | "CREATED"
  | "UPDATED"
  | "VALIDATED"
  | "MADE_AVAILABLE"
  | "ARCHIVED"
  | "RESTORED"
  | "DOCUMENT_CHANGED";

export interface ProfileActivity {
  id: string;
  type: ProfileActivityType;
  at: string;
}

export interface OAuthClientProfile {
  id: string;
  /** Set and verified from the trusted server session - never from a form. */
  tenantId: string;
  revision: string;
  name: string;
  description?: string;
  /** References existing tenant environment values. */
  environmentIds: string[];
  purposes: ProfilePurpose[];
  lifecycle: ProfileLifecycle;
  /** True once the profile has ever been available: its identity is fixed. */
  identityLocked: boolean;
  registration: ProfileRegistration;
  clientAuthentication?: ClientAuthenticationBinding;
  validation: ProfileValidation;
  metadataPreview?: ProfileMetadataPreview;
  usage: ProfileUsageSummary;
  createdAt: string;
  updatedAt: string;
  activity: ProfileActivity[];
  /** What this user may do with this record, derived by the server. */
  allowedActions: ProfileAction[];
}

export type ProfileFeatureState = "AVAILABLE" | "UNAVAILABLE";

export interface ProfileListCapabilities {
  /** UNAVAILABLE: this environment has no client-profile registry. */
  management: ProfileFeatureState;
  validation: ProfileFeatureState;
  usage: UsageCompleteness;
}

export interface ProfilePage {
  items: OAuthClientProfile[];
  total: number;
  capabilities: ProfileListCapabilities;
}

export type ProfileRegistrationInput =
  | { kind: "CIMD"; clientIdUrl?: string }
  | {
      kind: "EXISTING_OAUTH_CLIENT";
      clientId?: string;
      /** A provider integration reference - never a display name or URL. */
      providerId?: string;
      /** Legacy records only; the server resolves the issuer from `providerId`. */
      issuer?: string;
    };

/** The allowlisted write DTO: nothing derived, nothing secret. */
export interface ProfileInput {
  name: string;
  description?: string;
  environmentIds: string[];
  purposes: ProfilePurpose[];
  registration: ProfileRegistrationInput;
  managedCredentialRef?: string;
}

export interface CreateProfileRequest {
  input: ProfileInput;
  lifecycle: "DRAFT" | "AVAILABLE";
  /** Required for AVAILABLE: a passed check of exactly these inputs. */
  validationRef?: string;
  /** A repeated request with the same key returns the first result. */
  idempotencyKey: string;
}

export interface UpdateProfileRequest {
  id: string;
  /** Optimistic concurrency: a stale revision is rejected. */
  revision: string;
  input: ProfileInput;
  validationRef?: string;
  /** Draft → Available, when the current checks pass. */
  makeAvailable?: boolean;
  /** From `previewOAuthClientProfileImpactUsingPost`, for consequential edits. */
  impactToken?: string;
}

export interface ValidateProfileRequest {
  input: ProfileInput;
  /** A saved profile being re-checked; absent for an unsaved candidate. */
  profileId?: string;
}

export interface ProfileValidationJob {
  id: string;
  state: "CHECKING" | "DONE" | "CANCELLED";
  inputFingerprint: string;
  profileId?: string;
  startedAt: string;
  result?: ProfileValidation;
  preview?: ProfileMetadataPreview;
}

export type UsageReferenceType =
  | "MCP_CONNECTION"
  | "GATEWAY_DEFAULT"
  | "INHERITED_GATEWAY";

export interface ProfileUsageReference {
  id: string;
  type: UsageReferenceType;
  resourceId: string;
  resourceName: string;
  connectionName?: string;
  purposes: ProfilePurpose[];
  environmentName?: string;
}

export interface ProfileUsage {
  summary: ProfileUsageSummary;
  references: ProfileUsageReference[];
  /** Distinct agents across the referencing connections, or null if unknown. */
  affectedAgentCount: number | null;
}

export interface ProfileImpact {
  /** Changes that could affect existing references (credential, restrictions). */
  consequentialFields: Array<
    "managedCredentialRef" | "purposes" | "environmentIds"
  >;
  /** Registration identity changes: blocked once a profile was available. */
  identityChange: boolean;
  identityChangeBlocked: boolean;
  usage: ProfileUsageSummary;
  /** Usage is not fully known, so a consequential change cannot be assessed. */
  blocked: boolean;
  /** Pass back on the update to confirm this assessment was reviewed. */
  impactToken?: string;
}

export interface ProfileSelectionContext {
  registrationKind: "CIMD" | "EXISTING_OAUTH_CLIENT";
  purpose: ProfilePurpose;
  environmentId?: string;
  /** Unknown until the service's protected-resource metadata is read. */
  targetIssuer?: string;
  serviceConnectionId?: string;
  /** The value already on the form - kept even if no longer selectable. */
  currentProfileId?: string;
}

export type ProfileEligibility =
  | "ELIGIBLE"
  | "CONTEXT_CHECK_REQUIRED"
  | "INELIGIBLE"
  | "EXISTING_REFERENCE_ONLY";

export type EligibilityReason =
  | "DRAFT"
  | "ARCHIVED"
  | "VALIDATION_FAILED"
  | "VALIDATION_STALE"
  | "PURPOSE_RESTRICTED"
  | "ENVIRONMENT_RESTRICTED"
  | "CREDENTIAL_UNAVAILABLE"
  | "METHOD_MISMATCH"
  | "ISSUER_MISMATCH"
  | "ISSUER_UNSUPPORTED"
  | "ISSUER_UNKNOWN"
  | "CONTEXT_UNKNOWN";

export interface ProfileOption {
  id: string;
  revision: string;
  name: string;
  registrationKind: ProfileRegistrationKind;
  /** CIMD only: the client ID the form stores alongside the reference. */
  clientIdUrl?: string;
  environmentLabels: string[];
  purposes: ProfilePurpose[];
  lifecycle: ProfileLifecycle;
  validationState: ProfileCheckState;
  checkedAt?: string;
  eligibility: ProfileEligibility;
  reasonCodes: EligibilityReason[];
}

export type ProfileOptionsState =
  | "OK"
  | "FORBIDDEN"
  | "UNAVAILABLE"
  | "INACCESSIBLE_CURRENT";

export interface ProfileOptionsResult {
  state: ProfileOptionsState;
  options: ProfileOption[];
  /** The form's current reference, resolved separately from the choices. */
  current?: ProfileOption;
  /** Set when the current reference exists but this user may not see it. */
  currentInaccessible?: boolean;
  /** Profiles exist but none are complete - "setup is incomplete". */
  draftsOnly: boolean;
}

export interface ManagedCredentialOption {
  id: string;
  label: string;
  /** What the credential is for, e.g. "private_key_jwt". */
  method: string;
  availability: CredentialAvailability;
  /** Only returned when this user may see the referenced record. */
  environmentId?: string;
}

export interface ProfileFormOptions {
  environments: Array<{ id: string; name: string }>;
  credentials: ManagedCredentialOption[];
}

/** Where an issuer value came from, as the backend reports it. */
export type IssuerSource = "SAVED_CONFIGURATION" | "PROVIDER_METADATA";

export type ProviderVerification =
  | "NOT_CHECKED"
  | "VERIFIED"
  | "MISMATCH"
  | "UNAVAILABLE";

/**
 * One tenant-scoped authorization-provider integration - the place an
 * application is registered. Every value is the integration's configured or
 * backend-validated data; the browser never builds a URL from a name.
 */
export interface AuthorizationProvider {
  id: string;
  /** e.g. "Microsoft Entra ID". */
  name: string;
  /** Tenant / environment context that tells two integrations apart. */
  context?: string;
  /** The exact configured issuer. Absent: not configured. */
  issuer?: string;
  issuerSource?: IssuerSource;
  verification: ProviderVerification;
  /** The issuer the backend discovered, when it differs (MISMATCH). */
  discoveredIssuer?: string;
  endpoints?: {
    metadataUrl?: string;
    authorizationEndpoint?: string;
    tokenEndpoint?: string;
  };
  /**
   * A documented URL format for this provider type - guidance only. Never an
   * active value: it is not copied, verified, persisted or requested.
   */
  issuerFormat?: string;
  /** Demonstration configuration: no provider connection has been verified. */
  demo: boolean;
}

export interface AuthorizationProviderList {
  providers: AuthorizationProvider[];
}

export interface ProfileListQuery {
  search?: string;
  registrationKind?: ProfileRegistrationKind;
  lifecycle?: ProfileLifecycle | "NEEDS_ATTENTION";
}

/** Error codes the mock (and a future backend) raise; never raw bodies. */
export const PROFILE_ERRORS = {
  VERSION_CONFLICT: "VERSION_CONFLICT",
  DUPLICATE: "DUPLICATE_PROFILE",
  IDENTITY_LOCKED: "IDENTITY_LOCKED",
  VALIDATION_REQUIRED: "VALIDATION_REQUIRED",
  IMPACT_REVIEW_REQUIRED: "IMPACT_REVIEW_REQUIRED",
  USAGE_UNKNOWN: "USAGE_UNKNOWN",
  NOT_FOUND: "PROFILE_NOT_FOUND",
  NOT_ASSIGNABLE: "PROFILE_NOT_ASSIGNABLE",
  UNAVAILABLE: "PROFILE_REGISTRY_UNAVAILABLE",
  FORBIDDEN: "PROFILE_FORBIDDEN",
  INVALID: "PROFILE_INVALID",
} as const;

export type ProfileErrorCode =
  (typeof PROFILE_ERRORS)[keyof typeof PROFILE_ERRORS];

export interface ProfileError extends Error {
  code: ProfileErrorCode;
  /** DUPLICATE: the profile that already has this identity. */
  existingProfileId?: string;
}

export const profileErrorCode = (
  error: unknown,
): ProfileErrorCode | undefined => {
  const code = (error as { code?: unknown } | null)?.code;
  return typeof code === "string" &&
    (Object.values(PROFILE_ERRORS) as string[]).includes(code)
    ? (code as ProfileErrorCode)
    : undefined;
};

const mockOnly = (): never => {
  throw new Error(
    "OAuthClientProfileService is mock-only - add a src/configs/mock.ts entry and a src/api/mock-data registry entry.",
  );
};

/**
 * IMPORTANT: regular static METHODS, not static arrow fields - the mock layer
 * resolves registry entries by `method.name`.
 */
export class OAuthClientProfileService {
  static listOAuthClientProfilesUsingGet(
    _params: ProfileListQuery,
  ): Promise<ProfilePage> {
    return Promise.resolve(mockOnly());
  }

  static getOAuthClientProfileUsingGet(_params: {
    id: string;
  }): Promise<OAuthClientProfile> {
    return Promise.resolve(mockOnly());
  }

  static getOAuthClientProfileFormOptionsUsingGet(
    _params: Record<string, never>,
  ): Promise<ProfileFormOptions> {
    return Promise.resolve(mockOnly());
  }

  /** Authorization providers applicable to client profiles in this tenant. */
  static listOAuthAuthorizationProvidersUsingGet(
    _params: Record<string, never>,
  ): Promise<AuthorizationProviderList> {
    return Promise.resolve(mockOnly());
  }

  static getOAuthAuthorizationProviderUsingGet(_params: {
    id: string;
  }): Promise<AuthorizationProvider> {
    return Promise.resolve(mockOnly());
  }

  static createOAuthClientProfileUsingPost(
    _params: CreateProfileRequest,
  ): Promise<OAuthClientProfile> {
    return Promise.resolve(mockOnly());
  }

  static updateOAuthClientProfileUsingPatch(
    _params: UpdateProfileRequest,
  ): Promise<OAuthClientProfile> {
    return Promise.resolve(mockOnly());
  }

  /** Starts a backend check; the browser never fetches the document itself. */
  static validateOAuthClientProfileUsingPost(
    _params: ValidateProfileRequest,
  ): Promise<ProfileValidationJob> {
    return Promise.resolve(mockOnly());
  }

  static getOAuthClientProfileValidationUsingGet(_params: {
    jobId: string;
  }): Promise<ProfileValidationJob> {
    return Promise.resolve(mockOnly());
  }

  static cancelOAuthClientProfileValidationUsingPost(_params: {
    jobId: string;
  }): Promise<ProfileValidationJob> {
    return Promise.resolve(mockOnly());
  }

  static getOAuthClientProfileUsageUsingGet(_params: {
    id: string;
  }): Promise<ProfileUsage> {
    return Promise.resolve(mockOnly());
  }

  static previewOAuthClientProfileImpactUsingPost(_params: {
    id: string;
    input: ProfileInput;
  }): Promise<ProfileImpact> {
    return Promise.resolve(mockOnly());
  }

  /** Stops new assignments; existing references stay. Revokes nothing. */
  static archiveOAuthClientProfileUsingPost(_params: {
    id: string;
    revision: string;
  }): Promise<OAuthClientProfile> {
    return Promise.resolve(mockOnly());
  }

  /** Back to Draft: current checks are required before it is selectable. */
  static restoreOAuthClientProfileUsingPost(_params: {
    id: string;
    revision: string;
  }): Promise<OAuthClientProfile> {
    return Promise.resolve(mockOnly());
  }

  static listOAuthClientProfileOptionsUsingPost(
    _params: ProfileSelectionContext,
  ): Promise<ProfileOptionsResult> {
    return Promise.resolve(mockOnly());
  }
}
